import { createSignal, onMount, Show } from "solid-js"
import { clock } from "../stores/clock"

// Lazy load PostHog to avoid blocking initial render
let posthog: any = null
const loadPostHog = async () => {
    if (!posthog) {
        const module = await import("posthog-js")
        posthog = module.default
    }
    return posthog
}

export default function CookieConsent() {
    // Check if user has already decided
    const consentData = localStorage.getItem("cookie-consent")

    // Determine if we should show the banner
    const shouldShow = () => {
        if (!consentData) return true // No decision yet

        try {
            const data = JSON.parse(consentData)

            // If accepted, never show again
            if (data.status === "accepted") return false

            // If declined, check if 30 days have passed
            if (data.status === "declined" && data.declinedAt) {
                const declinedDate = new Date(data.declinedAt)
                const daysSinceDecline = (Date.now() - declinedDate.getTime()) / (1000 * 60 * 60 * 24)
                return daysSinceDecline >= 30
            }

            return true
        } catch {
            // Invalid/legacy data format - clear it and ask for consent again
            localStorage.removeItem("cookie-consent")
            return true
        }
    }

    // Safely parse consent data, handling legacy string format
    const getConsent = () => {
        if (!consentData) return null
        try {
            const data = JSON.parse(consentData)
            return data.status || null
        } catch {
            // Legacy format detected (old string "accepted") - clear it
            localStorage.removeItem("cookie-consent")
            return null
        }
    }

    const consent = getConsent()
    const [show, setShow] = createSignal(shouldShow())

    // Initialize PostHog on mount based on existing consent (deferred)
    onMount(async () => {
        // Defer PostHog loading to avoid blocking initial render
        await new Promise((resolve) => setTimeout(resolve, 2000))
        const ph = await loadPostHog()

        if (consent === "accepted") {
            // Full tracking for accepted users
            if (import.meta.env.PROD) {
                ph.init("phc_vUcIEvlrFZxVmC05xCjuOUArNW0hzmOyppX5lstf3Bg", {
                    api_host: "https://app.sim86.se",
                    ui_host: "https://us.posthog.com",
                    person_profiles: "identified_only",
                    session_recording: {
                        recordCanvas: false,
                        maskAllInputs: false,
                        // Sample mutations to reduce overhead from 60fps agent updates
                        // Agents will still appear in replays, just with slightly lower fidelity
                        mutationBreadcrumbMaxCount: 100, // Limit mutation breadcrumbs
                        sampling: {
                            scrollPositionThrottleMs: 250,
                            mousemoveThrottleMs: 100,
                        },
                    },
                    capture_pageview: true,
                    capture_pageleave: true,
                })

                // Send periodic heartbeat to maintain session accuracy during active viewing
                // Only when tab is in focus AND simulation is playing (true engagement)
                window.setInterval(() => {
                    if (!document.hidden && clock.isRunning) {
                        ph.capture("simulation_active_viewing")
                    }
                }, 30000) // Every 30 seconds during active playback
            }
        } else if (consent === "declined") {
            // Basic pageview tracking for declined users
            if (import.meta.env.PROD) {
                ph.init("phc_vUcIEvlrFZxVmC05xCjuOUArNW0hzmOyppX5lstf3Bg", {
                    api_host: "https://app.sim86.se",
                    ui_host: "https://us.posthog.com",
                    person_profiles: "identified_only",
                    disable_session_recording: true,
                    autocapture: false,
                    persistence: "memory", // Don't persist to cookies or localStorage
                })
                ph.capture("$pageview")
                ph.opt_out_capturing() // Prevent further events
            }
        }
    })

    const accept = async () => {
        localStorage.setItem(
            "cookie-consent",
            JSON.stringify({
                status: "accepted",
                acceptedAt: new Date().toISOString(),
            })
        )
        setShow(false)

        const ph = await loadPostHog()
        if (import.meta.env.PROD) {
            ph.init("phc_vUcIEvlrFZxVmC05xCjuOUArNW0hzmOyppX5lstf3Bg", {
                api_host: "https://app.sim86.se",
                ui_host: "https://us.posthog.com",
                person_profiles: "identified_only",
                session_recording: {
                    recordCanvas: false,
                    maskAllInputs: false,
                    mutationBreadcrumbMaxCount: 100,
                    sampling: {
                        scrollPositionThrottleMs: 250,
                        mousemoveThrottleMs: 100,
                    },
                },
                capture_pageview: true,
                capture_pageleave: true,
                loaded: (p: any) => {
                    p.capture("consent_accepted")

                    // Send periodic heartbeat during active simulation viewing
                    window.setInterval(() => {
                        if (!document.hidden && clock.isRunning) {
                            p.capture("simulation_active_viewing")
                        }
                    }, 30000)
                },
            })
        }
    }

    const decline = async () => {
        localStorage.setItem(
            "cookie-consent",
            JSON.stringify({
                status: "declined",
                declinedAt: new Date().toISOString(),
            })
        )
        setShow(false)

        const ph = await loadPostHog()
        if (import.meta.env.PROD) {
            ph.init("phc_vUcIEvlrFZxVmC05xCjuOUArNW0hzmOyppX5lstf3Bg", {
                api_host: "https://app.sim86.se",
                ui_host: "https://us.posthog.com",
                person_profiles: "identified_only",
                disable_session_recording: true,
                autocapture: false,
                persistence: "memory", // Don't persist to cookies or localStorage
                loaded: (p: any) => {
                    p.capture("$pageview")
                    p.opt_out_capturing()
                },
            })
        }
    }

    return (
        <Show when={show()}>
            <div class="fixed right-0 bottom-0 left-0 z-40 border-t border-gray-200 bg-white/95 shadow-lg backdrop-blur-sm select-none md:bottom-11">
                <div class="mx-auto max-w-7xl px-4 py-3 md:py-2">
                    {/* Desktop: horizontal layout */}
                    <div class="hidden items-center justify-between gap-8 sm:flex">
                        <p class="text-sm text-gray-700">
                            Vi använder cookies för att förbättra funktionaliteten och samla in övergripande statistik.
                        </p>
                        <div class="flex shrink-0 gap-3">
                            <button
                                onClick={decline}
                                class="rounded border border-gray-300 px-4 py-1 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                            >
                                Avböj
                            </button>
                            <button
                                onClick={accept}
                                class="rounded bg-green-600 px-4 py-1 text-sm font-medium text-white transition-colors hover:bg-green-700"
                            >
                                OK
                            </button>
                        </div>
                    </div>

                    {/* Mobile: stacked layout */}
                    <div class="space-y-3 sm:hidden">
                        <p class="text-center text-sm text-gray-700">
                            Vi använder cookies för att förbättra funktionaliteten och samla in övergripande statistik.
                        </p>
                        <div class="flex gap-3">
                            <button
                                onClick={decline}
                                class="flex-1 rounded border border-gray-300 py-2.5 text-sm text-gray-600 transition-colors hover:bg-gray-50"
                            >
                                Avböj
                            </button>
                            <button
                                onClick={accept}
                                class="flex-1 rounded bg-green-600 py-2.5 text-sm font-medium text-white transition-colors hover:bg-green-700"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Show>
    )
}
